/**
 * Escolha do icone de um habito ou de uma contagem regressiva.
 *
 * Os dois campos existiam no DDL, eram lidos e renderizados desde a fase 6 — e
 * **nao havia onde escolher**: tudo que a interface criava nascia sem icone.
 * Mesma classe de defeito da §8f, e o mesmo remedio da `ColorField`: um
 * componente so, porque habito e contagem precisam da MESMA nocao de "escolher
 * icone" e duas copias divergiriam na primeira vez que alguem mexesse no alvo
 * de toque.
 *
 * **Lista curta em vez de campo livre.** Um `<input>` aceita "correr" como
 * icone, e o card renderiza a palavra inteira dentro de um circulo de 32 px —
 * um campo que aceita o que nao cabe empurra para o usuario uma regra que
 * ninguem escreveu. A lista tambem resolve o teclado do celular: escolher e um
 * toque, contra abrir o seletor de emoji e procurar.
 *
 * O preco e nao dar conta de todo emoji que existe. Vale: o icone aqui e
 * reconhecimento rapido numa lista de dez itens, nao expressao.
 */

/** Emojis que cobrem os habitos e as contagens que este app costuma ter. */
const ICONES = [
  '🏃', '💧', '📖', '🧘', '💊', '🥗', '💪', '😴',
  '💰', '🎯', '✈️', '🎂', '🏖', '🎓', '🏠', '❤️',
] as const;

export function IconField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (icon: string | null) => void;
}) {
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="text-sm text-text-mut">{label}</legend>

      <div className="flex flex-wrap gap-1">
        {/*
          "Sem icone" e uma opcao explicita, e nao a ausencia de escolha: sem
          ela, quem escolheu um icone por engano nao teria como voltar atras —
          e nulo e o padrao de todo registro criado antes deste campo existir.
        */}
        <Opcao
          rotulo="Sem ícone"
          selecionado={value === null}
          onClick={() => onChange(null)}
        >
          <span aria-hidden="true" className="text-text-mut">
            —
          </span>
        </Opcao>

        {ICONES.map((icone) => (
          <Opcao
            key={icone}
            rotulo={icone}
            selecionado={value === icone}
            onClick={() => onChange(icone)}
          >
            <span aria-hidden="true">{icone}</span>
          </Opcao>
        ))}
      </div>
    </fieldset>
  );
}

function Opcao({
  rotulo,
  selecionado,
  onClick,
  children,
}: {
  rotulo: string;
  selecionado: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      // `aria-pressed` e nao `aria-selected`: sao botoes de alternancia, e nao
      // opcoes de um listbox — o leitor de tela anuncia "pressionado", que e o
      // que descreve o estado real.
      aria-pressed={selecionado}
      aria-label={rotulo}
      onClick={onClick}
      className={[
        'min-h-touch w-11 rounded-sm text-md transition-colors duration-150',
        selecionado ? 'bg-brand-blue-soft ring-1 ring-brand-blue' : 'bg-surface-2',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
